/**
 * Pure data transforms for the headline charts (Recharts-agnostic, so
 * they're unit-testable without rendering). The Recharts wrappers in
 * `ChartsPanel.tsx` consume these shapes directly.
 */
import type {
  BonusContribution,
  FeatureTriggerStats,
  Histogram,
} from "../engine/types";

/** Compact win-multiplier label, e.g. "0", "10", "1k", "5k", "∞". */
export function formatMultiplier(x: number): string {
  if (!Number.isFinite(x)) return "∞";
  if (x === 0) return "0";
  const abs = Math.abs(x);
  if (abs >= 1_000_000) return `${trim1(x / 1_000_000)}M`;
  if (abs >= 1_000) return `${trim1(x / 1_000)}k`;
  return trim1(x);
}

function trim1(x: number): string {
  // Up to one decimal place, no trailing ".0".
  return Number(x.toFixed(1)).toString();
}

/** Human label for a `[lo, hi)` win bucket: "<2×", "2–5×", "≥1k×". */
export function bucketLabel(lo: number, hi: number): string {
  if (lo === 0) return `<${formatMultiplier(hi)}×`;
  if (!Number.isFinite(hi)) return `≥${formatMultiplier(lo)}×`;
  return `${formatMultiplier(lo)}–${formatMultiplier(hi)}×`;
}

export interface WinBucketDatum {
  label: string;
  /** Spins landing in this bucket. `null` (not 0) for empty buckets so
   *  a log y-axis never has to plot `log(0)` — Recharts skips null bars. */
  count: number | null;
}

export function winDistributionData(
  histogram: Histogram | null | undefined,
): WinBucketDatum[] {
  return (histogram?.buckets ?? []).map((b) => ({
    label: bucketLabel(b.lo, b.hi),
    count: b.count === 0 ? null : b.count,
  }));
}

/**
 * Spec 013 — per-feature award win-distribution buckets for the
 * picker-selected feature. The per-feature award histogram reuses the
 * identical 15-bucket log `Histogram` shape, so this is a thin wrapper
 * over {@link winDistributionData}: pass the `Histogram` for the
 * selected feature (or `null`/`undefined` → `[]` for the empty state).
 */
export function perFeatureAwardHistogramData(
  histogram: Histogram | null | undefined,
): WinBucketDatum[] {
  return winDistributionData(histogram);
}

export interface PercentBarDatum {
  label: string;
  /** Value in percentage points (e.g. 0.21 → 21). */
  pct: number;
}

/**
 * A `Record<string, fraction>` (per-symbol or per-rule RTP contribution)
 * as percentage-point bars, largest first, capped at `topN`. Shared by
 * the "RTP by symbol" and "RTP by rule" charts.
 */
export function percentContributionBars(
  contributions: Record<string, number> | null | undefined,
  topN = 12,
): PercentBarDatum[] {
  return Object.entries(contributions ?? {})
    .map(([label, fraction]) => ({ label, pct: fraction * 100 }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, topN);
}

/** Per-feature trigger rate as percentage-point bars, largest first. */
export function featureTriggerBars(
  triggers: Record<string, FeatureTriggerStats> | null | undefined,
): PercentBarDatum[] {
  return Object.entries(triggers ?? {})
    .map(([label, stats]) => ({ label, pct: (stats?.rate ?? 0) * 100 }))
    .sort((a, b) => b.pct - a.pct);
}

export interface RtpSeriesPoint {
  /** 1-indexed spin number (x-axis). */
  spin: number;
  /** Cumulative RTP through this spin, in percentage points:
   *  `mean(winnings[0..spin-1]) * 100`. Converges toward long-run RTP. */
  cumulative: number;
  /** Rolling-window RTP in percentage points: mean over the last
   *  `min(spin, window)` spins * 100. Tracks local volatility. */
  rolling: number;
}

/**
 * Cumulative + rolling-window RTP series from a per-spin `winnings`
 * array (stake-multiples, so `RTP == mean(winnings)`). Both series are
 * computed at full resolution, then **downsampled to ~`maxPoints`
 * log-spaced spins** (always including spin 1 and the final spin) so the
 * chart pairs with a log x-axis: it starts at the y-axis, is dense over
 * the early high-variance spins (where convergence is most visible), and
 * sparse over the smooth tail. Log spacing collapses to fewer than
 * `maxPoints` points for small inputs (duplicate early targets dedupe).
 *
 * For spins before the window fills (`spin < window`), `rolling` is the
 * mean over the spins seen so far (i.e. equals `cumulative` early on),
 * avoiding a leading gap.
 */
export function computeRtpSeries(
  winnings: readonly number[],
  opts: { window?: number; maxPoints?: number } = {},
): RtpSeriesPoint[] {
  const window = Math.max(1, opts.window ?? 500);
  const maxPoints = Math.max(1, opts.maxPoints ?? 400);
  const n = winnings.length;
  if (n === 0) return [];

  // Log-spaced target spins (1-indexed): spin = round(n^(j/(maxPoints-1)))
  // for j in [0, maxPoints), which yields 1 at j=0 and n at the end. A Set
  // dedupes the dense low end (1,1,2,2,…) so the result is ≤ maxPoints.
  // Always include the final spin. Seed spin 1 and the log-spaced interior
  // only when there's room, so `maxPoints == 1` yields exactly one point.
  const targets = new Set<number>([n]);
  if (maxPoints > 1) {
    targets.add(1);
    if (n > 1) {
      const lnN = Math.log(n);
      for (let j = 0; j < maxPoints; j++) {
        const spin = Math.round(Math.exp((lnN * j) / (maxPoints - 1)));
        targets.add(Math.min(n, Math.max(1, spin)));
      }
    }
  }

  const points: RtpSeriesPoint[] = [];
  let cumSum = 0;
  let rollSum = 0;
  for (let i = 0; i < n; i++) {
    const w = winnings[i] ?? 0;
    cumSum += w;
    rollSum += w;
    if (i >= window) rollSum -= winnings[i - window] ?? 0;
    const k = i + 1; // spins seen so far
    if (targets.has(k)) {
      const rollCount = Math.min(k, window);
      points.push({
        spin: k,
        cumulative: (cumSum / k) * 100,
        rolling: (rollSum / rollCount) * 100,
      });
    }
  }
  return points;
}

export interface RuleTableRow {
  rule: string;
  hits: number;
  /** hits / nSpins, in percentage points. */
  hitRatePct: number;
  /** Average payout per hit in stake multiples:
   *  (rtpFraction * nSpins) / hits. null when hits === 0. */
  avgPay: number | null;
  /** RTP contribution in percentage points. */
  rtpPct: number;
}

/**
 * Join per-rule RTP contribution with per-rule hit counts into the
 * PAY RULES table rows, sorted by contribution (largest first). Union
 * of keys across both records; missing values default to 0.
 */
export function ruleTableRows(
  rtpPerRule: Record<string, number> | null | undefined,
  hitsPerRule: Record<string, number> | null | undefined,
  nSpins: number,
): RuleTableRow[] {
  if (nSpins <= 0) return [];
  const rules = new Set([
    ...Object.keys(rtpPerRule ?? {}),
    ...Object.keys(hitsPerRule ?? {}),
  ]);
  return [...rules]
    .map((rule) => {
      const rtp = rtpPerRule?.[rule] ?? 0;
      const hits = hitsPerRule?.[rule] ?? 0;
      return {
        rule,
        hits,
        hitRatePct: (hits / nSpins) * 100,
        avgPay: hits > 0 ? (rtp * nSpins) / hits : null,
        rtpPct: rtp * 100,
      };
    })
    .sort((a, b) => b.rtpPct - a.rtpPct);
}

export interface BonusBreakdown {
  /** Base-game RTP in percentage points (total RTP minus all bonus). */
  base: number;
  /** Each bonus feature's contribution in percentage points, largest first. */
  features: { name: string; pct: number }[];
}

/**
 * Break total RTP into base vs each bonus feature's contribution
 * (percentage points), from the canonical `bonus_contribution`
 * (`{total, per_feature}`). Features are sorted largest-first. `base` is
 * clamped at 0 in case a noisy early-streaming sample reports the bonus
 * total momentarily exceeding the running total RTP.
 */
export function bonusBreakdownData(
  rtpPoint: number | null | undefined,
  bonus: BonusContribution | null | undefined,
): BonusBreakdown {
  const features = Object.entries(bonus?.per_feature ?? {})
    .map(([name, frac]) => ({ name, pct: (frac ?? 0) * 100 }))
    .sort((a, b) => b.pct - a.pct);
  const bonusTotal = (bonus?.total ?? 0) * 100;
  const base = Math.max(0, (rtpPoint ?? 0) * 100 - bonusTotal);
  return { base, features };
}
